import React, { useMemo, useRef, useCallback } from "react";
import styled from "styled-components";

import { ReactComponent as DeleteSvg } from '../../../image/Delete.svg';
import moment from "moment";
import CoachProfileImages from './CoachProfileImages';
import { convertMinuteToRatio } from '../../Share/Normal/useClassTime.js';
import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme, getColor } from '../../../Styles/theme';
import { useLanguage } from '../../../Context/LanguageContext';

// 높이 계산 상수
const CELL_HEIGHT = 83;  // 수업 셀 기본 높이
const COACH_HEIGHT = 31;  // 코치 사진 영역 높이 (Box 아래 sibling으로 배치)
const TIME_RATE_THRESHOLD = 1.5;  // 시간 비율 임계값
const HOVER_OFFSET = 2;  // 호버 시 빼는 높이

// 호버링 배경색 컬러 코드에서 RGB값으로 변경
function hexToRGB(hex, alpha) {
  let r = parseInt(hex.slice(1, 3), 16);
  let g = parseInt(hex.slice(3, 5), 16);
  let b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ")";
  }
  else {
    return 'rgba(' + r + ', ' + g + ', ' + b + ")";
  }
}

export default function ScheduleClassBox(props) {
  const {
    data,
    canceledSchedules,
    conversionRate,
    onClickWeeklyScheduleBox,
    onClickDeletedClass
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  const DeleteTotalRef = useRef();
  const DeleteBackgroundRef = useRef();
  const DeleteButtonRef = useRef();
  const touchStartRef = useRef(null);

  // 폐강 여부 확인 //
  const isCanceled = useMemo(() => {
    return canceledSchedules.some(canceled =>
      data.classDate === moment(canceled.classDate).format('YYYY.MM.DD') &&
      data.classTime === canceled.classTime &&
      data.scheduleId === canceled.scheduleId
    );
  }, [data, canceledSchedules]);

  // 수업 시간 문자열을 시간 차이(시간 단위)로 계산 //
  const timeDifference = useMemo(() => {
    const [startTime, endTime] = data.classTime.split('-');

    const [startTimeHour, startTimeMinute] = startTime.split(':').map(Number);
    const [endTimeHour, endTimeMinute] = endTime.split(':').map(Number);

    const startValue = startTimeHour + convertMinuteToRatio(startTimeMinute);
    const endValue = endTimeHour + convertMinuteToRatio(endTimeMinute);

    return endValue - startValue;
  }, [data.classTime]);

  // 삭제 버튼 배경색 미리 계산
  const deleteBackgroundColor = useMemo(() => {
    return hexToRGB('#FF3131', 0.4);
  }, []);

  // 테두리 형태 및 색상 결정 //
  const border = useMemo(() => {
    const classColor = getColor(data.color, isDark);
    if (data.className === '수업없음') {
      return `2px solid ${theme.buttonFill}`;
    }
    else if (isCanceled) {
      return `2px dashed ${classColor}`;
    }
    else {
      return `2px solid ${classColor}`;
    }
  }, [data.className, isCanceled, data.color, isDark, theme]);

  // 수업 시간에 따른 크기 변환 계산 //
  const tdHeight = useMemo(() => {
    if (timeDifference < TIME_RATE_THRESHOLD) {
      return CELL_HEIGHT;
    }
    else {
      return (((timeDifference - 1) * conversionRate) + 1) * CELL_HEIGHT;
    }
  }, [timeDifference, conversionRate]);

  // 수업 시간에 따른 크기 변환 ( 호버링 전용 ) 계산 //
  const hoverTdHeight = useMemo(() => {
    if (isCanceled) {
      return tdHeight;
    }
    else {
      if (timeDifference < TIME_RATE_THRESHOLD) {
        return CELL_HEIGHT - HOVER_OFFSET;
      }
      else {
        return tdHeight - HOVER_OFFSET;
      }
    }
  }, [tdHeight, timeDifference, isCanceled]);

  // 코치 영역 유무에 따라 Box 자체 높이 조정 (BoxWarpper의 전체 높이는 tdHeight 유지) //
  const hasCoach = data.className !== '브레이크 타임';
  const boxHeight = hasCoach ? tdHeight - COACH_HEIGHT : tdHeight;
  const boxHoverHeight = hasCoach ? hoverTdHeight - COACH_HEIGHT : hoverTdHeight;

  // 수업 컴포넌트 개별 호버링 시 //
  const onMouseEnterTimeComponent = useCallback(() => {
    DeleteTotalRef.current.style.opacity = `1`;
  }, []);

  // 수업 컴포넌트 개별 호버링시 //
  const onMouseLeaveTimeComponent = useCallback(() => {
    DeleteTotalRef.current.style.opacity = `0`;
  }, []);

  // CSS 방식으로 변경하여 주석 처리 //
  // // 수업삭제 버튼 호버링시 //
  // function onMouseEnterDeleteButton() {
  //   DeleteBackgroundRef.current.style.width = '123px';
  //   DeleteBackgroundRef.current.style.height =  'calc(100% - 4px)';
  //   DeleteBackgroundRef.current.style.borderRadius = '5px';
  //   DeleteBackgroundRef.current.style.top = '1px';
  //   DeleteBackgroundRef.current.style.left = '0px';
  // }

  // // 수업삭제 버튼 호버리시 //
  // function onMouseLeaveDeleteButton() {
  //   DeleteBackgroundRef.current.style.margin = '0px';
  //   DeleteBackgroundRef.current.style.width = '20px';
  //   DeleteBackgroundRef.current.style.height = '17px';
  //   DeleteBackgroundRef.current.style.borderRadius = '0px 5px 0px 5px';
  //   DeleteBackgroundRef.current.style.top = '1px';
  //   DeleteBackgroundRef.current.style.left = '0px';
  // }

  const onClickWeeklyScheduleBoxIn = useCallback((e) => {
    if (DeleteButtonRef.current.contains(e.target)) {
      return;
    }

    onClickWeeklyScheduleBox(data);
  }, [data, onClickWeeklyScheduleBox]);

  return (
    <BoxWarpper
      height={tdHeight}
      backgroundColor={isCanceled ? theme.subBackground : getColor(data.color, isDark)}
      onMouseEnter={onMouseEnterTimeComponent}
      onMouseLeave={onMouseLeaveTimeComponent}>
      <DeleteButtonWrapper
        ref={DeleteTotalRef}
        isBreakTime={data.className === '브레이크 타임'}>
        <DeleteBackground
          ref={DeleteBackgroundRef}
          backgroundColor={deleteBackgroundColor}>
          <DeleteButton
            ref={DeleteButtonRef}
            onClick={() => onClickDeletedClass(data, 'Component')}>
            <StyledDeleteIcon />
          </DeleteButton>
        </DeleteBackground>
      </DeleteButtonWrapper>
      <Box
        isCanceled={isCanceled}
        onClick={(e) => onClickWeeklyScheduleBoxIn(e)}
        isValid={data.className !== '수업없음'}
        hasCoach={hasCoach}
        height={boxHeight}
        hoverHeight={boxHoverHeight}
        // color={data.className === '수업없음' ? '#ECECEC' : data.color}
        border={border}
        backgroundColor={isCanceled ? theme.subBackground : getColor(data.color, isDark)}>
        <TextWrapper>
          <ClassNameWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={isCanceled ? theme.subText : theme.white} cursor='pointer'>{data.className}</Text>
          </ClassNameWrapper>
          <ClassTimeWrapper>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={isCanceled ? theme.subText : theme.white} cursor='pointer'>{data.classTime}</Text>
          </ClassTimeWrapper>
        </TextWrapper>
      </Box>
      {data.className !== '브레이크 타임' && (
        <CoachTopWrapper>
          <CoachImageWrapper>
            <CoachProfileImages
              teacherProfile={data.teacherProfile}
              teacherProfiles={data.teacherProfiles}
              backgroundColor={getColor(data.color, isDark)} />
          </CoachImageWrapper>
          <CoachWrapperBackground isCanceled={isCanceled} backgroundColor={getColor(data.color, isDark)}>
            <CoachWrapper>
              <CoachNameWrapper>
                {isCanceled
                  ? <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.white} cursor='pointer'>{t('classManage.canceled')}</Text>
                  : <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.white} cursor='pointer'>{data.teacherName}</Text>
                }
              </CoachNameWrapper>
            </CoachWrapper>
          </CoachWrapperBackground>
        </CoachTopWrapper>
      )}
    </BoxWarpper>
  )
}

const BoxWarpper = styled.div`
  position: relative;

  width: 121px;
  height: ${props => props.height}px;

  border-radius: 5px;
`;

const Box = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  box-sizing: border-box;
  width: 121px;
  height: ${props => props.height}px;

  border-radius: ${props => props.hasCoach ? '5px 5px 0 0' : '5px'};
  background-color: ${props => props.backgroundColor};
  box-sizing: border-box;

  transition: all 0.3s ease-in-out;

  ${props => props.isCanceled && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      border-top: ${props.border};
      border-left: ${props.border};
      border-right: ${props.border};
      border-radius: 5px 5px 0 0;
      pointer-events: none;
    }
  `}

  @media (pointer: fine) and (hover: hover) {
    &:hover {
      cursor: ${props => props.isValid ? `pointer` : `default`};

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(255, 255, 255, 0.2);
        border-radius: 5px 5px 0 0;
        pointer-events: none;
        z-index: 3;
      }

      ${props => !props.isCanceled && `
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border: ${props.border};
          border-radius: 5px;
          pointer-events: none;
        }
      `}
    }
  }
`;

const DeleteButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  left: -1px;

  width: 123px;
  height: calc(100% + 4px);

  z-index: 5;
  opacity: 0;

  border-radius: 5px 5px 5px 5px;

  overflow: hidden;
  background-color: transparent;

  /* 자기 자신은 마우스 이벤트 통과 — BoxWarpper의 onMouseEnter가 정상 동작하도록 */
  pointer-events: none;

  @media (hover: none) {
    display: ${props => props.isBreakTime ? 'flex' : 'none'};
  }
`;

const DeleteBackground = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-end;

  z-index: 3;

  width: 20px;
  height: 17px;

  left: -1px;

  border-radius: 0px 5px 0px 5px;
  background-color: ${props => props.backgroundColor};

  transition: all 0.3s ease-in-out;

  @media (pointer: fine) and (hover: hover) {
    &:hover {
      width: 121px;
      height: calc(100% - 4px);
      border-radius: 5px;
    }
  }
`;

const DeleteButton = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 20px;
  height: 17px;

  border-radius: 0px 5px 0px 5px;
  background-color: ${props => props.theme.pointRed};

  z-index: 6;

  /* DeleteButtonWrapper의 pointer-events: none을 다시 복원 — 클릭 가능 */
  pointer-events: auto;

  &:hover {
    cursor: pointer;
  }

  transition: none;
`;

const StyledDeleteIcon = styled(DeleteSvg)`
  flex-shrink: 0;
  width: 12px;
  height: 12px;

  color: ${props => props.theme.white};
`;

const ClassNameWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  width: 95px;
  height: 16px;

  overflow: hidden;
  padding: 0 5px 5px 5px;
  // border: 1px solid;

  & > p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 100%;
  }
`;

const ClassTimeWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 110px;
  height: 16px;

  overflow: hidden;
  padding: 0 5px 3px 5px;
  // border: 1px solid;
`;

const CoachTopWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  margin-top: auto;

  width: 121px;
  height: 31px;

  overflow: hidden;

  border-radius: 0 0 5px 5px;

  z-index: 4;
`;

const CoachWrapperBackground = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  margin-top: auto;

  width: 121px;
  height: 31px;

  overflow: hidden;

  border-radius: 0 0 5px 0;
  background-color: ${props => props.backgroundColor};
`;

const CoachWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  margin-top: auto;

  width: 121px;
  height: 31px;

  overflow: hidden;

  border-radius: 0 0 5px 0;
  background-color: rgba(255, 255, 255, 0.2);
`;

const CoachImageWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;

  width: 31px;
  height: 31px;
`;

const CoachNameWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;

  justify-content: ${props => props.isCanceled ? 'center' : 'flex-start'};
  align-items: center;

  width: 100%;

  padding: 0 5px 0 5px;
  box-sizing: border-box;

  z-index: 4;

  & > p {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 84px;
  }
`;

const TextWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  margin-top: 2px;

  width: 107px;
  height: 52px;

  z-index: 4;

  // border: 1px solid;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (pointer: fine) and (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }
`;